import { useEffect, useState } from 'react';
import { useAuthTenantId, useAuthUserId } from '@/stores/auth.store';
import { useTenantId } from '@/stores/tenant.store';
import { tenantScopedKey } from '@/lib/tenantStorage';

export type DispatchQueueView = 'list' | 'stages';

function readView(key: string | null): DispatchQueueView {
  try {
    return key && window.localStorage.getItem(key) === 'stages' ? 'stages' : 'list';
  } catch {
    return 'list';
  }
}

/** A personal display preference, isolated across accounts and partners. */
export function useDispatchQueueView() {
  const userId = useAuthUserId();
  const activeTenantId = useTenantId();
  const authTenantId = useAuthTenantId();
  const tenantId = activeTenantId ?? authTenantId;
  const key =
    userId && tenantId ? tenantScopedKey(`dispatch-queue-view::u:${userId}`, tenantId) : null;
  const [selection, setSelection] = useState<{ key: string | null; view: DispatchQueueView }>();
  // Resolve a new account/tenant synchronously, so the previous choice never leaks for a render.
  const view = selection?.key === key ? selection.view : readView(key);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) setSelection({ key, view: readView(key) });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  const setView = (next: DispatchQueueView) => {
    setSelection({ key, view: next });
    try {
      if (key) window.localStorage.setItem(key, next);
    } catch {
      // The choice still works for this session when browser storage is unavailable.
    }
  };
  return [view, setView] as const;
}
